import React, { useCallback, useEffect, useRef } from "react";
import { type DefaultRootState, connect } from "react-redux";
import styled from "styled-components";
import { setToolbarSnack as setToolbarSnackAction } from "actions/context";
import { CheckmarkIcon } from "components/icons/CheckmarkIcon";
import { ExclamationCircleIcon } from "components/icons/ExclamationCircleIcon";

import { toolbarSnackSelector } from "selectors/context";
import type { IToolbarSnack } from "types/context";

interface IStyledSnack {
  type: "info" | "error" | "success";
}

const StyledSnack = styled.div<IStyledSnack>`
  @keyframes slide {
    from {
      transform: translateY(-100%);
    }
    to {
      transform: translateY(0);
    }
  }
  animation: slide 0.5s;
  background-color: ${(props) => {
    if (props.type === "error") {
      return "#FFE7E3";
    }
    if (props.type === "success") {
      return "#D9EED4";
    }
    return props.theme.colors.dsGrayBG;
  }};
  border: ${(props) => props.theme.lightBorder};
  box-shadow: 5px 4px 5px rgba(0, 0, 0, 0.1);
  color: ${(props) => props.theme.colors.dsGrayText};
  cursor: pointer;
  line-height: ${(props) => props.theme.iconSize.small};
  padding: ${(props) => props.theme.spacing.small};
  position: absolute;
  text-align: center;
  top: 100%;
  width: 100%;
  z-index: 1;
  & * {
    vertical-align: middle;
  }
  & svg {
    margin-right: ${(props) => props.theme.spacing.small};
  }
`;

const ICONS = {
  info: ExclamationCircleIcon,
  success: CheckmarkIcon,
  error: ExclamationCircleIcon,
};

interface IToolbarSnacks extends IDispatchFromProps {
  snacks: IToolbarSnack[];
}

interface ITimeouts {
  [index: string]: number;
}

function ToolbarSnacks({ snacks, setToolbarSnack }: IToolbarSnacks) {
  const timeouts = useRef<ITimeouts>({});
  const clearTimeout = useCallback(
    (snackId: string) => {
      if (timeouts.current[snackId]) {
        window.clearTimeout(timeouts.current[snackId]);
        delete timeouts.current[snackId];
      }
    },
    [timeouts]
  );

  // TODO - display multiple snacks at once?
  const firstSnack = snacks[0];
  const dismiss = useCallback(() => {
    clearTimeout(firstSnack.id);
    setToolbarSnack({
      id: firstSnack.id,
      message: null,
      type: "info",
    });
  }, [firstSnack]);

  useEffect(() => {
    if (firstSnack) {
      const { id, timeout } = firstSnack;
      if (timeout) {
        timeouts.current[id] = window.setTimeout(dismiss, timeout);
      }
      return () => clearTimeout(firstSnack.id);
    }
  }, [dismiss, firstSnack]);

  return firstSnack ? (
    <StyledSnack type={firstSnack.type} onClick={dismiss}>
      {(() => {
        const IconComp = ICONS[firstSnack.type];
        return <IconComp />;
      })()}
      <span>{firstSnack.message}</span>
    </StyledSnack>
  ) : null;
}

interface IDispatchFromProps {
  setToolbarSnack: (snack: IToolbarSnack) => void;
}

const mapStateToProps = (state: DefaultRootState) => ({
  snacks: toolbarSnackSelector(state),
});

export default connect(mapStateToProps, {
  setToolbarSnack: setToolbarSnackAction,
})(ToolbarSnacks);
